import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  IconButton
} from "@mui/material";
import TrophyIcon from "@mui/icons-material/EmojiEvents";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../Api/instance";

export default function TestLeaderboardModal({
  open,
  onClose,
  testName,
  paperId,
  catId = 0,
  institutionId = 0,
  paperTitle = "",
}) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && testName && paperId) {
      fetchLeaderboard();
    }
  }, [open, testName, paperId, catId, institutionId]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post("get/U_GetTestLeaderboard.php", {
        testName: testName,
        paperId: paperId,
        catId: catId,
        institutionId: institutionId,
        limit: 50,
      });

      if (Array.isArray(res.data)) {
        setLeaderboard(res.data);
      } else if (res.data?.leaderboard && Array.isArray(res.data.leaderboard)) {
        setLeaderboard(res.data.leaderboard);
      } else {
        setLeaderboard([]);
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
      setError("Failed to load leaderboard.");
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1)
      return (
        <Chip
          label="🥇 #1"
          style={{ backgroundColor: "#ffd700", color: "#000", fontWeight: 800 }}
          size="small"
        />
      );
    if (rank === 2)
      return (
        <Chip
          label="🥈 #2"
          style={{ backgroundColor: "#c0c0c0", color: "#000", fontWeight: 800 }}
          size="small"
        />
      );
    if (rank === 3)
      return (
        <Chip
          label="🥉 #3"
          style={{ backgroundColor: "#cd7f32", color: "#fff", fontWeight: 800 }}
          size="small"
        />
      );
    return (
      <Typography variant="body2" style={{ fontWeight: 700, textAlign: "center", color: "#64748b" }}>
        #{rank}
      </Typography>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
          padding: "18px 24px",
        }}
      >
        <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <TrophyIcon style={{ color: "#f59e0b", fontSize: "28px" }} />
          <Box>
            <Typography variant="h6" style={{ fontWeight: 800 }}>
              Test Leaderboard
            </Typography>
            {paperTitle && (
              <Typography variant="caption" color="textSecondary">
                {paperTitle}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" style={{ color: "#64748b" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: "20px" }}>
        {loading ? (
          <Box style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "200px", gap: "12px" }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="textSecondary">
              Loading rankings...
            </Typography>
          </Box>
        ) : error ? (
          <Typography color="error" style={{ textAlign: "center", padding: "20px" }}>
            {error}
          </Typography>
        ) : leaderboard.length === 0 ? (
          <Typography color="textSecondary" style={{ textAlign: "center", padding: "30px" }}>
            No leaderboard rankings recorded yet for this test.
          </Typography>
        ) : (
          <TableContainer component={Paper} elevation={0} style={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
            <Table>
              <TableHead style={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell style={{ fontWeight: 700, width: "80px" }}>Rank</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>Student Name</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>Correct</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>Negative</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>Net Score</TableCell>
                  <TableCell align="right" style={{ fontWeight: 700 }}>Attempt Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboard.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    style={{ backgroundColor: index < 3 ? "rgba(245, 158, 11, 0.04)" : "inherit" }}
                  >
                    <TableCell>{getRankBadge(row.rank)}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{row.student_name}</TableCell>
                    <TableCell align="center" style={{ color: "#16a34a", fontWeight: 700 }}>
                      {row.correct_count}
                    </TableCell>
                    <TableCell align="center" style={{ color: "#dc2626", fontWeight: 600 }}>
                      -{row.negative_marks}
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography style={{ color: "#1d4ed8", fontWeight: 800, fontSize: "0.95rem" }}>
                          {row.net_score}
                        </Typography>
                        {row.percentage > 0 && (
                          <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                            {row.percentage}%
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: "0.85rem", color: "#64748b" }}>
                      {row.test_date}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={onClose} variant="contained" color="inherit" style={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
